import { BaseService } from "../BaseService";
import { BookingBaseService } from "./BookingBaseService";
import { AuthService } from "../auth/auth.service";
import { environment } from '../../../../src/environments/environment';
import { HttpClient } from "@angular/common/http";
import { Router } from '@angular/router';
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { ApiResult } from "../../models/ApiResult";
import { BookingModel } from "../../models/api/BookingModel";
import { BookingFindResultModel } from "../../models/api/BookingFindResultModel";
import { Injectable } from "@angular/core";
import { BookingRequest } from "../../models/api/BookingRequest";
import { BookingResponse } from "../../models/api/BookingResponse";
import { BookingStatusResponse } from "../../models/api/BookingStatusResponse";
import { BookingModelExtra } from "../../models/api/BookingModelExtra";
import { AdminBookingInfo } from "../../models/api/AdminBookingInfo";
import { Grid_BookingModel } from "../../models/api/Grid_BookingModel";

@Injectable({
  providedIn: 'root'
})

export class BookingService extends BookingBaseService {

    constructor(private h: HttpClient, private r: Router, private a: AuthService) {
        super(h, r, a);
    };

  /// <summary>
  /// POST: Adds a new Booking record to the database
  /// /Booking/request
  /// </summary>
  /// <param name="request"></param>
  /// <returns></returns>
  public bookingRequest(request: BookingRequest): Observable<BookingResponse> {
    
    let url = `${super.getBaseUrl() + '/request/'}`;

    // Check if we're from the app, if so, check for a device guid
    const ua = navigator.userAgent;

    if (ua.includes('WHAppAndroid')) {

      const match = ua.match(/deviceGuid=([a-z0-9\-]+)/i);
      const deviceGuid = match ? match[1] : null;

      if (deviceGuid) {
        url += `?deviceguid=${deviceGuid}&mobileos=2`;
      }
    }
    else if (ua.includes('WHAppIOS')) {
      // Yes, its an app, and it's ios, so check for device string
      const match = ua.match(/deviceGuid=([a-z0-9\-]+)/i);
      const deviceGuid = match ? match[1] : null;

      if (deviceGuid) {
        url += `?deviceguid=${deviceGuid}&mobileos=1`;
      } 
    }

    if (!environment.production)
      console.log("Booking Request:Calling " + url, request);

    return this.h.post<BookingResponse>(url, request)
      .pipe(
        map(next => {
          let o: BookingResponse = Object.assign(new BookingResponse(), next);
          return o;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// GET: Gets a status for a booking
  /// /Booking/status/{reference}
  /// </summary>
  /// <param name="reference"></param>
  /// <returns></returns>
  public bookingStatus(reference: String): Observable<BookingStatusResponse> {

    const url = `${super.getBaseUrl() + '/status/' + reference}`;

    if (!environment.production)
      console.log("Booking Status:Calling " + url);

    return this.h.get<BookingStatusResponse>(url)
      .pipe(
        map(next => {
          let o: BookingStatusResponse = Object.assign(new BookingStatusResponse(), next);
          return o;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Updates a Booking record from subset of fields
  /// /Booking/updatemini
  /// </summary>
  /// <param name="request"></param>
  /// <returns></returns>
  public bookingUpdateMini(reference: string, request: BookingRequest): Observable<BookingModel> {

    const url = `${super.getBaseUrl() + '/updatemini/' + reference}`;

    if (!environment.production)
      console.log("Booking Update Mini:Calling " + url, request);

    return this.h.put<BookingModel>(url, request)
      .pipe(
        map(next => {
          let o: BookingModel = Object.assign(new BookingModel(), next);
          return o;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Cancels a Booking by updating it's status
  /// /Booking/cancel/{id}
  /// </summary>
  /// <param name="id"></param>
  /// <returns></returns>
  public  cancelBooking(guid: string): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/cancel/' + guid}`;

    if (!environment.production)
      console.log("Cancel Booking:Calling " + url, guid);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// GET: Gets the next booking
  /// /Booking/next
  /// </summary>
  /// <returns></returns>
  public getNextBooking(): Observable<Grid_BookingModel> {

    const url = `${super.getBaseUrl() + '/next'}`;

    if (!environment.production)
      console.log("Booking Status:Calling " + url);

    return this.h.get<Grid_BookingModel>(url)
      .pipe(
        map(next => {
          let o: Grid_BookingModel = Object.assign(new Grid_BookingModel(), next);
          return o;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// GET: Gets the current booking
  /// /Booking/current
  /// </summary>
  /// <returns></returns>
  public getCurrentBooking(): Observable<BookingModelExtra> {

    const url = `${super.getBaseUrl() + '/current'}`;

    if (!environment.production)
      console.log("Booking Status:Calling " + url);

    return this.h.get<BookingModelExtra>(url)
      .pipe(
        map(next => {
          let o: BookingModelExtra = Object.assign(new BookingModelExtra(), next);
          return o;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Resets a booking
  /// /Booking/reset/<bookingid>
  /// </summary>
  /// <param name="bookingid"></param>
  /// <returns></returns>
  public bookingReset(bookingid: number): Observable<BookingModel> {

    const url = `${super.getBaseUrl() + '/reset/' + bookingid}`;

    if (!environment.production)
      console.log("Booking Reset:Calling " + url);

    return this.h.put<BookingModel>(url, null)
      .pipe(
        map(next => {
          let o: BookingModel = Object.assign(new BookingModel(), next);
          return o;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// FIND: Retrieves a list of Booking records from the database
  /// /Booking
  /// </summary>
  public getAdminBookingList(): Observable<AdminBookingInfo[]> {
    // Find Booking Records

    const url = `${super.getBaseUrl()}/adminbookinglist`;

    if (!environment.production)
      console.log("Get Admin Booking List: Calling " + url);

    return this.h.get<AdminBookingInfo[]>(url)
      .pipe(
        map(next => next), // Simply pass through the array
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Cancels a Booking by updating it's status (Admins)
  /// /Booking/admincancel/{bookingid}
  /// </summary>
  /// <param name="bookingid"></param>
  /// <returns></returns>
  public adminCancelBooking(bookingid: number): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/admincancel/' + bookingid}`;

    if (!environment.production)
      console.log("Cancel Booking:Calling " + url, bookingid);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// GET: Retrieves a list of Booking records from the database, by event
  /// /Booking
  /// </summary>
  public getAdminBookingListByEvent(eventID: number): Observable<AdminBookingInfo[]> {
    // Find Booking Records

    const url = `${super.getBaseUrl()}/adminbookinglistbyevent/` + eventID;

    if (!environment.production)
      console.log("Get Admin Booking List By Event: Calling " + url);

    return this.h.get<AdminBookingInfo[]>(url)
      .pipe(
        map(next => next), // Simply pass through the array
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }
}
